import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { useSite } from "../../context/SiteContext";
import { Overline } from "../Reveal";
import FieldLines from "../FieldLines";

export default function Philosophy() {
    const { site, tx } = useSite();
    const c = site?.content || {};
    const items = c.philosophy || [];
    const ref = useRef(null);
    const [idx, setIdx] = useState(0);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

    useMotionValueEvent(scrollYProgress, "change", (v) => {
        if (!items.length) return;
        setIdx(Math.min(items.length - 1, Math.floor(v * items.length)));
    });

    if (!items.length) return null;
    const current = items[idx];

    return (
        <section ref={ref} id="philosophy" data-testid="philosophy-section" className="relative bg-[#0A0A0A] text-white" style={{ height: `${items.length * 100}vh` }}>
            <div className="sticky top-0 flex h-screen items-center overflow-hidden px-6 md:px-12 lg:px-24">
                <FieldLines className="absolute -bottom-64 -right-64 h-[42rem] w-[42rem] opacity-50" />
                <div className="relative grid w-full items-center gap-10 md:grid-cols-12">
                    <div className="md:col-span-4">
                        <Overline testId="philosophy-overline">{tx(c.philosophy_title) || "OUR PHILOSOPHY"}</Overline>
                        <div className="relative mt-6 h-[9rem] md:h-[16rem]">
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={current.num}
                                    initial={{ opacity: 0, y: 40 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -40 }}
                                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    className="font-display text-outline-w absolute text-[8rem] leading-none md:text-[15rem]"
                                    data-testid="philosophy-number"
                                >
                                    {current.num}
                                </motion.span>
                            </AnimatePresence>
                        </div>
                        <div className="mt-4 flex gap-2">
                            {items.map((_, i) => (
                                <span key={i} className={`h-px w-10 transition-colors duration-500 ${i === idx ? "bg-[#D60024]" : "bg-white/25"}`} />
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <AnimatePresence mode="wait">
                            <motion.h3
                                key={idx}
                                initial={{ opacity: 0, y: 60 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -60 }}
                                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                className="font-display text-[clamp(2.6rem,6.5vw,6rem)] leading-[0.9]"
                                data-testid="philosophy-statement"
                            >
                                {tx(current.text)}
                            </motion.h3>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}
